"use client";

import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { usePathname } from "next/navigation";
import { getCurrentDomain } from "@/lib/domainConfig";
import {
  Billboard,
  Mobile,
  BillboardContainer,
} from "@/components/Ads/AdComponent";
import ProsperAd from "@/components/Ads/ProsperAd";

interface DomainAdSwitcherProps {
  placement?:
    | "billboard"
    | "leaderboard"
    | "mpu"
    | "mobile_banner"
    | "mobile_mpu"
    | "double_mpu"
    | "video"
    | "desktop_takeover"
    | "mobile_takeover"
    | "skyscraper";
  alias?: string;
  className?: string;
}

export default function DomainAdSwitcher({
  placement = "billboard",
  alias,
  className,
}: DomainAdSwitcherProps) {
  const [domain, setDomain] = useState<"gumgum" | "tcgfan" | "default">(
    "default",
  );
  const [isClient, setIsClient] = useState(false);
  const pathname = usePathname();
  const isDesktopOrLaptop = useMediaQuery({
    query: "(min-width: 1250px)",
  });

  useEffect(() => {
    setIsClient(true);
    // Determine domain from environment or current host
    const currentDomain = getCurrentDomain(window.location.host);
    setDomain(currentDomain);
  }, []);

  // Don't render until client-side hydration is complete
  if (!isClient) {
    return (
      <BillboardContainer>
        <div />
      </BillboardContainer>
    );
  }

  const isDesktop = isDesktopOrLaptop;
  const isMobileView = !isDesktopOrLaptop;

  // For gumgum.gg, use existing Venatus ads
  if (domain === "gumgum") {
    return (
      <BillboardContainer>
        <Billboard isVisible={isDesktop} />
        <Mobile isVisible={isMobileView} />
      </BillboardContainer>
    );
  }

  // For tcgfan.com
  if (domain === "tcgfan") {
    // Don't show top ads (billboard, leaderboard) on the landing page only
    const isLandingPage = pathname === "/";
    if (
      isLandingPage &&
      (placement === "billboard" ||
        placement === "leaderboard" ||
        placement === "desktop_takeover" ||
        placement === "mobile_takeover")
    ) {
      return null;
    }

    // For all placements, use Prosper ads
    let prosperPlacement = placement;

    // Auto-select responsive placements based on screen size
    if (placement === "mpu") {
      prosperPlacement = isMobileView ? "mobile_mpu" : "mpu";
    }

    return (
      <BillboardContainer>
        <ProsperAd
          placementName={prosperPlacement}
          alias={alias}
          className={className}
        />
      </BillboardContainer>
    );
  }

  // Default behavior for localhost/other domains - show existing ads
  return (
    <BillboardContainer>
      <Billboard isVisible={isDesktop} />
      <Mobile isVisible={isMobileView} />
    </BillboardContainer>
  );
}

// Specific placement components for easier usage
export function ProsperBillboard({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <DomainAdSwitcher
      placement="billboard"
      alias={alias}
      className={className}
    />
  );
}

export function ProsperLeaderboard({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <DomainAdSwitcher
      placement="leaderboard"
      alias={alias}
      className={className}
    />
  );
}

export function ProsperMPU({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <DomainAdSwitcher placement="mpu" alias={alias} className={className} />
  );
}

export function ProsperVideo({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <DomainAdSwitcher placement="video" alias={alias} className={className} />
  );
}

export function ProsperDesktopTakeover({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <DomainAdSwitcher
      placement="desktop_takeover"
      alias={alias}
      className={className}
    />
  );
}

export function ProsperMobileTakeover({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <DomainAdSwitcher
      placement="mobile_takeover"
      alias={alias}
      className={className}
    />
  );
}
